import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Instagram, Facebook, Youtube, Linkedin, Twitter, Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { api, CATEGORIES, waLink } from "../../lib/api";
import { Logo } from "./Header";

const SOCIAL = { instagram: Instagram, facebook: Facebook, youtube: Youtube, linkedin: Linkedin, x: Twitter };

export function Footer() {
  const { site } = useSite();
  const [dests, setDests] = useState([]);
  useEffect(() => {
    api.get("/public/destinations").then((r) => setDests(r.data.filter((d) => d.featured).slice(0, 6))).catch(() => {});
  }, []);
  const c = site?.contact || {};
  const year = new Date().getFullYear();
  return (
    <footer className="bg-black text-white on-dark" data-testid="site-footer">
      <div className="container-x py-16 lg:py-24 grid gap-12 md:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-4 space-y-5">
          <Logo site={site} />
          <p className="text-sm text-white/70 max-w-xs leading-relaxed">{site?.footer?.description}</p>
          <div className="flex gap-4">
            {Object.entries(site?.social || {}).filter(([, v]) => v).map(([k, v]) => {
              const Icon = SOCIAL[k] || MessageCircle;
              return <a key={k} href={v} target="_blank" rel="noopener noreferrer" aria-label={k} data-testid={`footer-social-${k}`} className="hover:opacity-70"><Icon className="h-5 w-5" /></a>;
            })}
          </div>
        </div>
        <FooterCol title="Experiences" links={CATEGORIES.map((x) => ({ to: x.path, label: x.label }))} />
        <FooterCol title="Destinations" links={[...dests.map((d) => ({ to: `/destinations/${d.slug}`, label: d.name })), { to: "/destinations", label: "All destinations" }, { to: "/blog", label: "Blog" }]} />
        <div className="lg:col-span-3 space-y-3 text-sm">
          <h3 className="eyebrow !text-white/60 mb-4">Contact</h3>
          {c.email ? <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:opacity-70" data-testid="footer-email"><Mail className="h-4 w-4" />{c.email}</a> : <p className="text-white/50">[Email — add in Admin]</p>}
          {c.phone ? <a href={`tel:${c.phone}`} className="flex items-center gap-2 hover:opacity-70" data-testid="footer-phone"><Phone className="h-4 w-4" />{c.phone}</a> : <p className="text-white/50">[Phone — add in Admin]</p>}
          <a href={waLink(site?.whatsapp?.number, site?.whatsapp?.default_message)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-70" data-testid="footer-whatsapp"><MessageCircle className="h-4 w-4" />WhatsApp us</a>
          {c.address && <p className="flex gap-2 text-white/70"><MapPin className="h-4 w-4 shrink-0 mt-0.5" />{c.address}</p>}
        </div>
      </div>
      <div className="border-t border-[#333]">
        <div className="container-x py-6 flex flex-col md:flex-row gap-4 justify-between text-xs text-white/60">
          <p>© {year} {site?.footer?.copyright}</p>
          <div className="flex flex-wrap gap-5">
            <Link to="/privacy-policy" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms">Terms and Conditions</Link>
            <Link to="/cancellation-policy" data-testid="footer-cancellation">Cancellation and Refund Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

const FooterCol = ({ title, links }) => (
  <div className="lg:col-span-2.5 lg:col-span-2 space-y-3">
    <h3 className="eyebrow !text-white/60 mb-4">{title}</h3>
    {links.map((l) => <Link key={l.to} to={l.to} className="block text-sm text-white/85 hover:text-white hover:underline underline-offset-4">{l.label}</Link>)}
  </div>
);
